import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../apiClient';
import './MortgageDetails.css';

const MortgageDetails = ({ propertyId, propertyName, onClose, embedded = false }) => {
  const { instance } = useMsal();
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showAddRate, setShowAddRate] = useState(false);

  useEffect(() => {
    fetchTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      const data = await apiFetchJson(instance, `/api/properties/${propertyId}/MortgageTimeline`);
      setTimeline(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const content = () => {
    if (loading) return <div className="loading">Loading payment history...</div>;
    if (error) return <div className="error">Error: {error}</div>;

    const rawEvents = timeline.events;
    const events = rawEvents?.$values && Array.isArray(rawEvents.$values) ? rawEvents.$values : Array.isArray(rawEvents) ? rawEvents : [];

    return (
      <>
        {/* Action Buttons */}
        <div className="action-buttons">
          <button onClick={() => setShowRecordPayment(true)} className="btn-primary">
            Record Payment
          </button>
          <button onClick={() => setShowAddRate(true)} className="btn-secondary">
            Add Rate Change
          </button>
        </div>

        {/* Timeline */}
        <div className="timeline-section">
          <h4>Payment History & Timeline</h4>
          {events.length === 0 ? (
            <p className="empty-state">No payments or rate changes recorded yet.</p>
          ) : (
            <div className="timeline">
              {events.map((event) => (
                <div key={event.id} className={`timeline-event ${event.type}`}>
                  <div className="event-date">
                    {new Date(event.date).toLocaleDateString('nb-NO')}
                  </div>
                  <div className="event-content">
                    <div className="event-description">{event.description}</div>
                    {event.type === 'payment' && (
                      <div className="event-details">
                        <span className="detail">Interest: {event.interestPortion?.toLocaleString('nb-NO')} kr</span>
                        <span className="detail">Principal: {event.principalPortion?.toLocaleString('nb-NO')} kr</span>
                        <span className="detail">Balance: {event.balanceAfter?.toLocaleString('nb-NO')} kr</span>
                        {event.isExtraPayment && <span className="badge extra">Extra</span>}
                      </div>
                    )}
                    {event.type === 'rate_change' && (
                      <div className="event-details">
                        <span className="detail rate">Rate: {event.interestRate?.toFixed(2)}%</span>
                      </div>
                    )}
                    {event.notes && <div className="event-notes">{event.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modals */}
        {showRecordPayment && (
          <RecordPaymentModal
            propertyId={propertyId}
            onClose={() => setShowRecordPayment(false)}
            onSuccess={() => {
              setShowRecordPayment(false);
              fetchTimeline();
            }}
          />
        )}
        {showAddRate && (
          <AddRateChangeModal
            propertyId={propertyId}
            onClose={() => setShowAddRate(false)}
            onSuccess={() => {
              setShowAddRate(false);
              fetchTimeline();
            }}
          />
        )}
      </>
    );
  };

  // Embedded mode: render inline without overlay
  if (embedded) {
    return <div className="mortgage-details-embedded">{content()}</div>;
  }

  // Modal mode: original overlay behavior
  return (
    <div className="mortgage-details-overlay" onClick={onClose}>
      <div className="mortgage-details-container" onClick={(e) => e.stopPropagation()}>
        <div className="mortgage-header">
          <div>
            <h2>Mortgage Details</h2>
            <p className="property-name">{propertyName}</p>
          </div>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>
        {content()}
      </div>
    </div>
  );
};

// Record Payment Modal
const RecordPaymentModal = ({ propertyId, onClose, onSuccess }) => {
  const { instance } = useMsal();
  const [payment, setPayment] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    totalPayment: '',
    isExtraPayment: false,
    notes: ''
  });
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (payment.totalPayment && parseFloat(payment.totalPayment) > 0) {
      fetchPreview();
    } else {
      setPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.totalPayment, payment.isExtraPayment]);

  const fetchPreview = async () => {
    try {
      const data = await apiFetchJson(instance, `/api/properties/${propertyId}/payments/preview`, {
        method: 'POST',
        body: JSON.stringify({
          totalPayment: parseFloat(payment.totalPayment),
          isExtraPayment: payment.isExtraPayment
        })
      });
      setPreview(data);
    } catch (err) {
      console.error('Preview error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await apiFetchJson(instance, `/api/properties/${propertyId}/payments`, {
        method: 'POST',
        body: JSON.stringify({
          paymentDate: payment.paymentDate,
          totalPayment: parseFloat(payment.totalPayment),
          isExtraPayment: payment.isExtraPayment,
          notes: payment.notes
        })
      });

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Record Payment</h3>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Payment Date</label>
            <input
              type="date"
              value={payment.paymentDate}
              onChange={(e) => setPayment({ ...payment, paymentDate: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Total Payment (kr)</label>
            <input
              type="number"
              step="0.01"
              value={payment.totalPayment}
              onChange={(e) => setPayment({ ...payment, totalPayment: e.target.value })}
              placeholder="Enter payment amount"
              required
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={payment.isExtraPayment}
                onChange={(e) => setPayment({ ...payment, isExtraPayment: e.target.checked })}
              />
              <span>Extra Payment (nedbetalingslån)</span>
            </label>
          </div>

          {/* Preview */}
          {preview && (
            <div className="preview-section">
              <h4>Payment Breakdown Preview</h4>
              <div className="preview-grid">
                <div className="preview-item">
                  <span>Interest:</span>
                  <span className="interest">{preview.interestPortion.toLocaleString('nb-NO')} kr</span>
                </div>
                <div className="preview-item">
                  <span>Principal:</span>
                  <span className="principal">{preview.principalPortion.toLocaleString('nb-NO')} kr</span>
                </div>
                <div className="preview-item">
                  <span>Balance After:</span>
                  <span className="balance">{preview.balanceAfterPayment.toLocaleString('nb-NO')} kr</span>
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              value={payment.notes}
              onChange={(e) => setPayment({ ...payment, notes: e.target.value })}
              placeholder="Add any notes about this payment"
              rows="3"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting || !preview}>
              {submitting ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Rate Change Modal
const AddRateChangeModal = ({ propertyId, onClose, onSuccess }) => {
  const { instance } = useMsal();
  const [rateChange, setRateChange] = useState({
    effectiveDate: new Date().toISOString().split('T')[0],
    interestRate: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await apiFetchJson(instance, `/api/properties/${propertyId}/interest-rates`, {
        method: 'POST',
        body: JSON.stringify({
          effectiveDate: rateChange.effectiveDate,
          interestRate: parseFloat(rateChange.interestRate),
          notes: rateChange.notes
        })
      });

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Interest Rate Change</h3>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Effective Date</label>
            <input
              type="date"
              value={rateChange.effectiveDate}
              onChange={(e) => setRateChange({ ...rateChange, effectiveDate: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Interest Rate (%)</label>
            <input
              type="number"
              step="0.01"
              value={rateChange.interestRate}
              onChange={(e) => setRateChange({ ...rateChange, interestRate: e.target.value })}
              placeholder="e.g., 4.50"
              required
            />
          </div>

          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              value={rateChange.notes}
              onChange={(e) => setRateChange({ ...rateChange, notes: e.target.value })}
              placeholder="e.g., Bank notified rate increase"
              rows="3"
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Add Rate Change'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MortgageDetails;
